import { useCallback } from "react";
import { useSetAtom } from "jotai";
import { useApi } from "./useApi";
import { useSessionSetAtom } from "../useSessionSetAtom";
import type {
    OptionValueDto,
    CreateOptionValueRequest,
    CreateOptionValueResponse,
    UpdateOptionValueRequest,
} from "@models/option";
import {
    optionValuesAtom,
    isFetchingOptionValuesAtom,
    isProcessingOptionValuesAtom,
} from "@/store/optionValueAtoms";

const OPTIONS_ENDPOINT = "/options";

export const useOptionValueApi = () => {
    const { callApi } = useApi();
    // Session-bound: an answer landing after sign-out must not refill it.
    const setOptionValues = useSessionSetAtom(optionValuesAtom);
    const setIsFetchingOptionValues = useSetAtom(isFetchingOptionValuesAtom);
    const setIsProcessingOptionValues = useSetAtom(isProcessingOptionValuesAtom);

    const getOptionValues = useCallback(
        async (optionId: number): Promise<OptionValueDto[]> => {
            setIsFetchingOptionValues(true);
            try {
                const data = await callApi<OptionValueDto[]>(
                    `${OPTIONS_ENDPOINT}/${optionId}/optionValues`,
                );
                setOptionValues(data);
                return data;
            } finally {
                setIsFetchingOptionValues(false);
            }
        },
        [callApi, setOptionValues, setIsFetchingOptionValues],
    );

    const getOptionValue = useCallback(
        async (optionId: number, optionValueId: number): Promise<OptionValueDto> => {
            setIsFetchingOptionValues(true);
            try {
                return await callApi<OptionValueDto>(
                    `${OPTIONS_ENDPOINT}/${optionId}/optionValues/${optionValueId}`,
                );
            } finally {
                setIsFetchingOptionValues(false);
            }
        },
        [callApi, setIsFetchingOptionValues],
    );

    const createOptionValue = useCallback(
        async (
            optionId: number,
            data: CreateOptionValueRequest,
        ): Promise<CreateOptionValueResponse> => {
            setIsProcessingOptionValues(true);
            try {
                return await callApi<CreateOptionValueResponse>(
                    `${OPTIONS_ENDPOINT}/${optionId}/optionValues`,
                    { method: "POST", body: JSON.stringify(data) },
                );
            } finally {
                setIsProcessingOptionValues(false);
            }
        },
        [callApi, setIsProcessingOptionValues],
    );

    const updateOptionValue = useCallback(
        async (
            optionId: number,
            optionValueId: number,
            data: UpdateOptionValueRequest,
        ): Promise<void> => {
            setIsProcessingOptionValues(true);
            try {
                await callApi(
                    `${OPTIONS_ENDPOINT}/${optionId}/optionValues/${optionValueId}`,
                    { method: "PUT", body: JSON.stringify(data) },
                );
            } finally {
                setIsProcessingOptionValues(false);
            }
        },
        [callApi, setIsProcessingOptionValues],
    );

    const deleteOptionValue = useCallback(
        async (optionId: number, optionValueId: number): Promise<void> => {
            setIsProcessingOptionValues(true);
            try {
                await callApi(
                    `${OPTIONS_ENDPOINT}/${optionId}/optionValues/${optionValueId}`,
                    { method: "DELETE" },
                );
            } finally {
                setIsProcessingOptionValues(false);
            }
        },
        [callApi, setIsProcessingOptionValues],
    );

    return {
        getOptionValues,
        getOptionValue,
        createOptionValue,
        updateOptionValue,
        deleteOptionValue,
    };
};
